/**
 * Orientation "forward" d'un modèle de voiture sur la piste — partagée entre
 * `circuit3dPreview.ts` (course) et `carOrientationTool.ts` (recadrage).
 *
 * Le nez du modèle est celui enregistré dans le `.glb` via l'outil de recadrage.
 * Plus de décalage runtime par slug : il s'ajoutait au fichier déjà tourné et
 * annulait le recadrage en course.
 */

/** Les modèles du Car Kit pointent vers l'axe -Z du kit plutôt que le forward attendu par la case. */
export const CAR_EXTRA_ROTATION = Math.PI / 2;

/** @deprecated Plus appliqué au rendu ; le recadrage du `.glb` fait foi. */
export const CAR_ORIENTATION_OVERRIDE_DEG: Readonly<Record<string, number>> = Object.freeze({});

/** Extrait le nom de fichier (sans extension) d'une URL de modèle `.glb`. */
export function carModelSlugFromUrl(url: string): string | undefined {
  const pathOnly = url.split(/[?#]/, 1)[0] ?? url;
  const match = /([^/\\]+)\.glb$/i.exec(pathOnly);
  return match?.[1];
}

/** Ajoute `?v=` (ou `&v=`) pour forcer le rechargement après un recadrage. */
export function withCarGlbCacheBust(url: string, version: number | string): string {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const sep = withoutHash.includes('?') ? '&' : '?';
  return `${withoutHash}${sep}v=${encodeURIComponent(String(version))}${hash}`;
}

let carGlbEpoch = Date.now();

/** Invalide le cache HTTP / Vite des `.glb` voiture (après recadrage dans la session). */
export function bumpCarGlbEpoch(): void {
  carGlbEpoch = Date.now();
}

export function getCarGlbEpoch(): number {
  return carGlbEpoch;
}

export function cacheBustCarGlbUrl(url: string): string {
  return withCarGlbCacheBust(url, carGlbEpoch);
}

/**
 * Orientation "forward" (radians) d'un modèle : toujours 0.
 * Le recadrage (rotation du fichier `.glb`) est la seule source de vérité.
 */
export function computeCarOrientationFix(_slug?: string): number {
  return 0;
}

/** Rotation Y (radians) pour un cap continu `(dx, dy)` en grille (Y → Z monde).
 * Sur un virage, `dx`/`dy` sont la tangente de l’arc — pas un palier cardinal.
 * `Math.atan2(dx, dy)` coïncide déjà avec `facingRotationY` (le +π/2 Kenney
 * est le cap est) : ne pas rajouter `CAR_EXTRA_ROTATION`. */
export function forwardRotationY(orientationFix: number, dx: number, dy: number): number {
  if (dx === 0 && dy === 0) {
    return facingRotationY(orientationFix, 0);
  }
  return orientationFix + Math.atan2(dx, dy);
}

/**
 * Correctif yaw des voitures live sur un virage d’**une seule case** (`armCells === 0`).
 *
 * Après le calcul correct de `zeroIsEntry` (voir `buildCornerArcMap`), la tangente
 * d’arc suit en général déjà le sens de circulation : le +180° systématique
 * historique retournait alors les modèles (ex. Green Land, virage gauche avant S/F).
 * On ne bascule de π que si la tangente s’oppose encore clairement au cap milieu
 * (entrée+sortie) — filet de sécurité si l’arc est à l’envers.
 *
 * Exception : circuit nommé « Hell » — jamais de retournement (historique).
 */
export function shouldApplySingleCellCornerYawOffset(circuitName?: string | null): boolean {
  const normalized = circuitName?.trim().toLowerCase();
  if (!normalized) return true;
  return normalized !== 'hell';
}

export interface SingleCellCornerYawContext {
  /** Tangente d’arc courante (`arcTravelForward`). */
  arcForward?: { dx: number; dy: number } | null;
  /** Direction d’entrée dans la case (stepIn, delta grille). */
  travelIn?: { dx: number; dy: number } | null;
  /** Direction de sortie (stepOut / facingSteps, delta grille). */
  travelOut?: { dx: number; dy: number } | null;
}

/**
 * +π seulement si `armCells === 0`, le circuit n’est pas Hell, et la tangente
 * d’arc (si fournie) s’oppose au cap milieu entrée→sortie.
 * Sans contexte géométrique : 0 (la tangente issue de `zeroIsEntry` fait foi).
 */
export function singleCellCornerCarYawOffsetRadians(
  armCells: number | null | undefined,
  circuitName?: string | null,
  geometry?: SingleCellCornerYawContext | null
): number {
  if (armCells !== 0) return 0;
  if (!shouldApplySingleCellCornerYawOffset(circuitName)) return 0;
  const arc = geometry?.arcForward;
  const travelIn = geometry?.travelIn;
  const travelOut = geometry?.travelOut;
  if (arc && travelIn && travelOut) {
    const ex = travelIn.dx + travelOut.dx;
    const ey = travelIn.dy + travelOut.dy;
    if (ex * ex + ey * ey > 1e-12) {
      return arc.dx * ex + arc.dy * ey < 0 ? Math.PI : 0;
    }
  }
  return 0;
}

/** Cap voiture live : tangente d’arc + correctif virage 1-case (si tangente à l’envers). */
export function liveCarForwardRotationY(
  orientationFix: number,
  dx: number,
  dy: number,
  armCells?: number | null,
  circuitName?: string | null,
  geometry?: SingleCellCornerYawContext | null
): number {
  return (
    forwardRotationY(orientationFix, dx, dy) +
    singleCellCornerCarYawOffsetRadians(armCells, circuitName, {
      arcForward: geometry?.arcForward ?? { dx, dy },
      travelIn: geometry?.travelIn,
      travelOut: geometry?.travelOut,
    })
  );
}

/** Rotation Y (radians) à appliquer à un modèle pour qu'il fasse face à `facingSteps`
 * (sens de circulation, 0-3) sur la case — voir `circuit3dPreview.ts#resolveLivePosition`. */
export function facingRotationY(orientationFix: number, facingSteps: number): number {
  return orientationFix + -(facingSteps * Math.PI) / 2 + CAR_EXTRA_ROTATION;
}
